import type { SignatureMethod } from "@closer/shared";
import { SIGNATURE_METHOD_NAMES, SIGNATURE_METHODS } from "@closer/shared";
import { Button, FileInput, FormField, Input, Modal, Select, useToast } from "@closer/ui";
import { useMutation } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";

import { api } from "../../lib/apiClient";
import { useAuth } from "../../lib/authContext";
import { uploadFile } from "../../lib/fileUpload";

/**
 * MANUEL (ISLAK) İMZA KAYDI (CLOS-167 `[160]`, ADR-0035).
 *
 * ### ⚠ Bu pencere sağlayıcı imzasını TAKLİT ETMİYOR
 *
 * Ayrı bir eylem: yöntem (ıslak imza, nitelikli sertifika, yüz yüze), imza tarihi ve kanıt
 * belgesi ayrı ayrı soruluyor. Kaydı imzacı değil, ıslak imzayı sisteme geçiren yetkili
 * kullanıcı veriyor — bu ayrım ekranda da korunuyor.
 *
 * ### ⚠ Sistem kanıtı DOĞRULAMIYOR, KAYDEDİYOR
 *
 * Bir PDF'in gerçekten ıslak imzalı olduğunu yazılım doğrulayamaz; yapabildiği, kimin ne
 * zaman hangi kanıtla beyan ettiğini değişmez biçimde kaydetmek. Bu yüzden kanıt zorunlu
 * ve dosya, ek yükleme yolunun güvenlik kontrollerinden geçiyor.
 */
export function ContractManualSignatureDialog({
  open,
  contractId,
  hedef,
  onClose,
  onDone,
}: {
  open: boolean;
  contractId: string;
  hedef: { requestId: string; signerId: string } | null;
  onClose: () => void;
  onDone: () => Promise<void> | void;
}) {
  const { activeTenantId } = useAuth();
  const tenantId = activeTenantId!;
  const toast = useToast();

  const [method, setMethod] = useState<SignatureMethod>("wet_ink");
  const [signedAt, setSignedAt] = useState("");
  const [note, setNote] = useState("");
  const dosyaSecici = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!open) return;
    setMethod("wet_ink");
    setSignedAt(new Date().toISOString().slice(0, 10));
    setNote("");
    if (dosyaSecici.current) dosyaSecici.current.value = "";
  }, [open]);

  const kaydet = useMutation({
    mutationFn: async () => {
      if (!hedef) throw new Error("İmzacı seçilmedi");
      const dosya = dosyaSecici.current?.files?.[0];
      if (!dosya) throw new Error("Kanıt belgesi seçilmedi");

      const yuklenen = await uploadFile({
        file: dosya,
        domain: "contract",
        entityId: contractId,
        tenantId,
      });

      return api(
        `/api/contracts/${contractId}/signature-requests/${hedef.requestId}/signers/${hedef.signerId}/manual`,
        {
          method: "POST",
          tenantId,
          body: JSON.stringify({
            method,
            /** Gün seçimi günün sonuna sabitleniyor; gelecek tarihi sunucu reddediyor. */
            signedAt: new Date(`${signedAt}T12:00:00`).toISOString(),
            evidenceObjectId: yuklenen.storageObjectId,
            ...(note.trim() ? { note: note.trim() } : {}),
          }),
        },
      );
    },
    onSuccess: async () => {
      toast.success("Manuel imza kaydedildi");
      await onDone();
    },
    onError: (err: unknown) =>
      toast.error(err instanceof Error ? err.message : "Manuel imza kaydedilemedi"),
  });

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Manuel imza kaydet"
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Vazgeç
          </Button>
          <Button onClick={() => kaydet.mutate()} loading={kaydet.isPending} disabled={!signedAt}>
            Kaydet
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3">
        <p className="text-xs text-fg-soft">
          Bu kayıt, imzanın sistem dışında atıldığını beyan eder. Belgenin gerçekliği doğrulanmaz;
          kimin ne zaman hangi kanıtla beyan ettiği kaydedilir.
        </p>

        <FormField label="İmza yöntemi">
          <Select
            id="manuel-yontem"
            value={method}
            onChange={(olay) => setMethod(olay.target.value as SignatureMethod)}
          >
            {SIGNATURE_METHODS.map((m) => (
              <option key={m} value={m}>
                {SIGNATURE_METHOD_NAMES[m]}
              </option>
            ))}
          </Select>
        </FormField>

        <FormField label="İmza tarihi">
          <Input
            id="manuel-tarih"
            type="date"
            value={signedAt}
            onChange={(olay) => setSignedAt(olay.target.value)}
          />
        </FormField>

        <FormField label="Kanıt belgesi">
          <FileInput id="manuel-kanit" ref={dosyaSecici} aria-label="Kanıt belgesi seç" />
        </FormField>

        <FormField label="Not (opsiyonel)">
          <Input
            id="manuel-not"
            value={note}
            onChange={(olay) => setNote(olay.target.value)}
            placeholder="Örn. Islak imzalı nüsha kargoyla geldi"
          />
        </FormField>
      </div>
    </Modal>
  );
}
